import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { buildApplyPlan, draftFlags } from '../../analysis/draft'
import type { AnalysisDraft } from '../../analysis/draft'
import type { ApplyChoices } from '../../analysis/useAnalysis'
import { formatTime } from '../../lib/format'

export type AnalysisPreviewBarProps = {
  draft: AnalysisDraft
  videoDuration: number
  existingLines: number
  existingTakes: number
  reviewOpen: boolean
  applying: boolean
  onToggleReview: () => void
  onApply: (choices: ApplyChoices) => void
  onDismiss: () => void
}

export function AnalysisPreviewBar({
  draft,
  videoDuration,
  existingLines,
  existingTakes,
  reviewOpen,
  applying,
  onToggleReview,
  onApply,
  onDismiss,
}: AnalysisPreviewBarProps): JSX.Element {
  const [replaceExisting, setReplaceExisting] = useState(true)
  const [tracks, setTracks] = useState(draft.hasTracks)
  const [setModeReplace, setSetModeReplace] = useState(draft.hasTracks)

  // Дорожек в результате может не быть (разбор без разделения) — тогда и
  // создавать нечего, и режим полного дубляжа включать не на чем.
  useEffect(() => {
    if (!draft.hasTracks) {
      setTracks(false)
      setSetModeReplace(false)
    }
  }, [draft.hasTracks])

  const plan = useMemo(() => buildApplyPlan(draft, videoDuration), [draft, videoDuration])
  const flags = useMemo(() => draftFlags(draft), [draft])
  const suspicious = useMemo(
    () => draft.lines.filter((line) => (flags.get(line.key)?.length ?? 0) > 0).length,
    [draft.lines, flags],
  )
  const total = useMemo(
    () => draft.lines.reduce((sum, line) => sum + Math.max(0, line.end - line.start), 0),
    [draft.lines],
  )
  const nonverbal = useMemo(
    () => draft.lines.filter((line) => line.kind === 'nonverbal').length,
    [draft.lines],
  )

  const apply = (): void => {
    if (plan.lines.length === 0) return
    if (replaceExisting && existingLines > 0) {
      const takesNote =
        existingTakes > 0 ? ` вместе с записанными дублями (${existingTakes})` : ''
      const question = `Заменить ${existingLines} существующих реплик${takesNote}?`
      if (!window.confirm(question)) return
    }
    onApply({ tracks, replaceExisting, setModeReplace })
  }

  return (
    <div className="detect-bar">
      <div className="row wrap">
        <strong>Предпросмотр разбора: {draft.lines.length} реплик</strong>
        <span className="faint mono">суммарно {formatTime(total)}</span>
        <span className="faint">ролей: {plan.roles.length}</span>
        {nonverbal > 0 ? (
          <span className="badge nonverbal-badge" title="Крики, стоны, дыхание — реплики без слов">
            ◍ без слов: {nonverbal}
          </span>
        ) : null}
        {suspicious > 0 ? (
          <span className="badge" style={{ color: 'var(--warning)' }}>
            сомнительных: {suspicious}
          </span>
        ) : null}
        <span className="spacer" />
        <button type="button" onClick={onToggleReview} disabled={applying}>
          {reviewOpen ? 'Скрыть таблицу' : `Открыть таблицу (${draft.lines.length})`}
        </button>
      </div>

      <div className="row wrap">
        <label className="row check" style={{ margin: 0 }}>
          <input
            type="radio"
            name="analysis-apply-mode"
            checked={replaceExisting}
            disabled={applying}
            onChange={() => setReplaceExisting(true)}
          />
          <span>Заменить разметку ({existingLines})</span>
        </label>
        <label className="row check" style={{ margin: 0 }}>
          <input
            type="radio"
            name="analysis-apply-mode"
            checked={!replaceExisting}
            disabled={applying}
            onChange={() => setReplaceExisting(false)}
          />
          <span>Добавить к существующей</span>
        </label>

        <label className="row check" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={tracks}
            disabled={applying || !draft.hasTracks}
            onChange={(e) => {
              const next = e.currentTarget.checked
              setTracks(next)
              if (!next) setSetModeReplace(false)
            }}
          />
          <span>Создать дорожки M&E и реплик</span>
        </label>

        <label className="row check" style={{ margin: 0 }}>
          <input
            type="checkbox"
            checked={setModeReplace}
            disabled={applying || !tracks}
            onChange={(e) => setSetModeReplace(e.currentTarget.checked)}
          />
          <span>Перевести проект в полный дубляж</span>
        </label>
      </div>

      <div className="row wrap">
        <span className="faint">
          {draft.hasTracks
            ? 'M&E из разделения — не студийная: там, где реплика звучала поверх громкой музыки, останутся следы голоса.'
            : 'Разбор шёл без разделения — дорожек нет, режим полного дубляжа включать не на чем.'}
        </span>
        <span className="spacer" />
        <button type="button" className="ghost" disabled={applying} onClick={onDismiss}>
          Отбросить
        </button>
        <button
          type="button"
          className="primary"
          disabled={applying || plan.lines.length === 0}
          onClick={apply}
          title="Записать результат в проект"
        >
          {applying ? 'Применяю…' : `Применить разбор (${plan.lines.length})`}
        </button>
      </div>

      {plan.unnamed > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Ролей без имени: {plan.unnamed}. Они создадутся как «Говорящий N» — имена лучше
          проставить в таблице, там видно, кто сколько говорит.
        </p>
      ) : null}

      {replaceExisting && existingLines > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Замена удалит существующие реплики
          {existingTakes > 0 ? ` и записанные к ним дубли (${existingTakes})` : ''}. Отката нет —
          если сомневаетесь, выберите «Добавить».
        </p>
      ) : null}
    </div>
  )
}
